import { useRef, useState } from 'preact/hooks';
import { defaultToolbarActions, minidocToolbar, placeholder } from 'minidoc-editor';
import { useMinidoc } from '@components/minidoc';
import { ManualDom } from '@components/manual-dom';
import { needsHtmlSource, trimEmptyHtml } from './html-content';

export { needsHtmlSource, trimEmptyHtml };

type Props = {
  label?: string;
  hint?: string;
  placeholderText?: string;
  value: string;
  onChange(html: string): void;
  labelClass?: string;
  minHeightClass?: string;
  textareaClass?: string;
  textareaRows?: number;
};

function Editor({
  initialDoc,
  placeholderText,
  minHeightClass,
  onChange,
}: {
  initialDoc: string;
  placeholderText?: string;
  minHeightClass: string;
  onChange(html: string): void;
}) {
  const editor = useMinidoc({
    doc: initialDoc,
    middleware: () => [
      placeholder(placeholderText || ''),
      minidocToolbar([...defaultToolbarActions]),
    ],
    onChange,
  });

  return (
    <div class="border border-gray-300 rounded-lg ring-2 ring-transparent focus-within:ring-indigo-400">
      <header class="border-b flex">
        <ManualDom el={editor.toolbar.root} />
      </header>
      <ManualDom class={`grow mini-padded ${minHeightClass} *:p-3`} el={editor.root} />
    </div>
  );
}

/**
 * Rich text field for admin-authored HTML that we render with
 * `dangerouslySetInnerHTML`. Media is deliberately absent from the toolbar:
 * media cards need `ReadonlyMinidoc` to render, which these surfaces don't use.
 *
 * "Edit HTML" switches to a raw HTML box at any time, so a page can be pasted in
 * as custom HTML (asked for 2026-10-05 after #770 left new pages rich-text only).
 * Switching back is only offered while the HTML has nothing the editor would
 * strip: one keystroke in the editor would save the stripped version over the
 * page (see #770's audit of live pages losing styles, images and scripts).
 */
export function RichHtmlField({
  label,
  hint,
  placeholderText,
  value,
  onChange,
  labelClass = 'font-medium',
  minHeightClass = '*:min-h-32',
  textareaClass = 'border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono min-h-32',
  textareaRows = 6,
}: Props) {
  const lastEmitted = useRef(value);
  const mountKey = useRef(0);
  // Starts as an HTML box when the stored content is custom HTML; otherwise the
  // admin chooses.
  const [htmlSource, setHtmlSource] = useState(() => needsHtmlSource(value));
  const canUseEditor = !needsHtmlSource(value);

  // Remount the editor when the value changes from somewhere else — the wizard's
  // "Generate Content" button writes the body. A fresh editor is the reliable
  // reset: `setState` on a live editor can leave it unable to take typing.
  // This runs during render, not in an effect: an effect compares a value the
  // typist has already moved past, and remounts the editor mid-keystroke.
  // Compare trimmed, because callers may store an emptied field as ''.
  if (trimEmptyHtml(value) !== trimEmptyHtml(lastEmitted.current)) {
    lastEmitted.current = value;
    mountKey.current += 1;
  }

  return (
    // A div, not a label: a label forwards clicks in the text to the first toolbar button.
    <div class="flex flex-col gap-1">
      <div class="flex items-baseline justify-between gap-4">
        {label ? <span class={labelClass}>{label}</span> : <span />}
        <button
          type="button"
          class="text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed shrink-0"
          disabled={htmlSource && !canUseEditor}
          onClick={() => {
            if (htmlSource) {
              // Fresh editor from the current HTML.
              mountKey.current += 1;
            }
            setHtmlSource(!htmlSource);
          }}
        >
          {htmlSource ? 'Switch to rich text' : 'Edit HTML'}
        </button>
      </div>
      {hint && <p class="text-xs text-gray-500">{hint}</p>}
      {htmlSource ? (
        <>
          <textarea
            class={textareaClass}
            rows={textareaRows}
            value={value}
            onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
            placeholder={placeholderText}
          />
          {!canUseEditor && (
            <span class="text-xs text-gray-500">
              This HTML uses formatting the rich-text editor can't keep (like styles, images or
              scripts), so it stays in HTML.
            </span>
          )}
        </>
      ) : (
        <Editor
          key={mountKey.current}
          initialDoc={value}
          placeholderText={placeholderText}
          minHeightClass={minHeightClass}
          onChange={(html) => {
            lastEmitted.current = html;
            onChange(html);
          }}
        />
      )}
    </div>
  );
}
